import React from 'react';
import { useSelector } from 'react-redux';
import {
  BadgePercent,
  BookOpen,
  CalendarRange,
  ClipboardCheck,
  Images,
  UsersRound,
  LineChart,
  Megaphone,
  Search,
  Settings2,
  ShieldAlert,
  Users,
  Wallet,
} from 'lucide-react';

// Shared admin UI kit. Button/input class strings + layout primitives used by
// every /admin/* page so they stay visually consistent with the reference
// pages (Documentation, Itineraries, Payment request).

const btnBase =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-transition-colors tw-whitespace-nowrap disabled:tw-opacity-50 disabled:tw-cursor-not-allowed';

export const primaryBtn = `${btnBase} tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700`;
export const neutralBtn = `${btnBase} tw-bg-white tw-text-gray-700 tw-border tw-border-gray-300 hover:tw-bg-gray-50`;
export const dangerBtn = `${btnBase} tw-bg-red-600 tw-text-white hover:tw-bg-red-700`;
// Row-level destructive actions: quieter than a solid red button in every row.
export const dangerGhostBtn = `${btnBase} tw-bg-white tw-text-red-600 tw-border tw-border-red-200 hover:tw-bg-red-50`;
export const iconBtn =
  'tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg tw-border tw-border-gray-200 tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-700 tw-transition-colors disabled:tw-opacity-50';

export const inputClass =
  'tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-bg-white focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-outline-none';

export const labelClass =
  'tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1';

// Single source of truth for the admin sections — rendered by the header's
// "Admin" nav dropdown (and the mobile drawer).
export const ADMIN_SECTIONS = [
  {
    label: 'Ops approvals',
    path: '/admin/ops',
    icon: ClipboardCheck,
    description: 'Payment recovery, reimbursements, invitations and Q&A',
  },
  {
    label: 'Users',
    path: '/admin/users',
    icon: UsersRound,
    description: 'Account editing and Samurai duck invitations',
  },
  {
    label: 'Fulfilment',
    path: '/admin/fulfilment',
    icon: CalendarRange,
    description: 'Monthly trips overview with payments and predictions',
  },
  {
    label: 'Trip finances',
    path: '/admin/finance',
    icon: LineChart,
    description: 'Monthly GMV, costs and margins per trip',
  },
  {
    label: 'Marketing',
    path: '/admin/marketing',
    icon: Megaphone,
    description: 'Homepage banners, notifications and automations',
  },
  {
    label: 'Weekly deals',
    path: '/admin/weekly-deals',
    icon: BadgePercent,
    description: 'Curate and publish weekly offers',
  },
  {
    label: 'Landing pages',
    path: '/admin/guides',
    icon: BookOpen,
    description: 'SEO landing pages at sotravel.com/guides',
  },
  {
    label: 'Merchant payouts',
    path: '/merchant-payment',
    icon: Wallet,
    description: 'Travel-related merchant payment requests',
  },
  {
    label: 'Affiliates payouts',
    path: '/admin/payouts',
    icon: Wallet,
    description: 'Withdrawals owed to clubs and affiliates',
  },
  {
    label: 'Clubs',
    path: '/admin/clubs',
    icon: Users,
    description: 'Self-serve clubs, members and platform fee',
  },
  {
    label: 'Trip albums',
    path: '/admin/trip-album',
    icon: Images,
    description: 'Bulk upload trip photos & videos, grouped by person',
  },
  {
    label: 'Website configurations',
    path: '/admin/website_configurations',
    icon: Settings2,
    description: 'Project bulk & env updates',
  },
];

// Page shell for every /admin/* page: admin guard, section sub-nav, and a
// consistent header (semibold title + gray subtitle + right-aligned actions).
export function AdminPage({ title, subtitle, actions, maxWidthClass, children }) {
  const isAdmin = useSelector((s) => s.user.user.is_admin);

  if (!isAdmin) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50">
        <div className="tw-max-w-xl tw-mx-auto tw-px-4 tw-py-16 tw-text-center">
          <ShieldAlert className="tw-w-12 tw-h-12 tw-text-gray-300 tw-mx-auto tw-mb-3" />
          <h1 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-1">
            Sotravel staff only
          </h1>
          <p className="tw-text-sm tw-text-gray-500">
            This page requires an admin account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <div
        className={`tw-mx-auto tw-px-4 sm:tw-px-6 tw-py-6 ${
          maxWidthClass || 'tw-max-w-7xl'
        }`}
      >
        <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3 tw-mb-6">
          <div className="tw-min-w-0">
            <h1 className="tw-text-xl tw-font-semibold tw-text-gray-900">
              {title}
            </h1>
            {subtitle ? (
              <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-0">
                {subtitle}
              </p>
            ) : null}
          </div>
          {actions ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
              {actions}
            </div>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

// Underline tabs for switching sections within a page (sits below the pill
// sub-nav so the two levels of navigation read differently).
export function TabBar({ tabs, active, onChange }) {
  return (
    <div className="tw-flex tw-gap-6 tw-border-b tw-border-gray-200 tw-overflow-x-auto">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={`tw-pb-2.5 tw-text-sm tw-font-medium tw-whitespace-nowrap tw-border-b-2 tw--mb-px tw-transition-colors ${
            active === t.key
              ? 'tw-text-blue-600 tw-border-blue-600'
              : 'tw-text-gray-500 tw-border-transparent hover:tw-text-gray-700'
          }`}
        >
          {t.label}
          {t.count !== undefined && t.count !== null ? (
            <span
              className={`tw-ml-1.5 tw-px-1.5 tw-py-0.5 tw-text-xs tw-rounded-full ${
                active === t.key
                  ? 'tw-bg-blue-50 tw-text-blue-600'
                  : 'tw-bg-gray-100 tw-text-gray-500'
              }`}
            >
              {t.count}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, className }) {
  return (
    <div className={`tw-relative ${className || 'tw-w-full sm:tw-w-72'}`}>
      <Search className="tw-w-4 tw-h-4 tw-text-gray-400 tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-pointer-events-none" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Search…'}
        className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500"
      />
    </div>
  );
}

const PILL_TONES = {
  green: 'tw-bg-green-50 tw-text-green-600',
  amber: 'tw-bg-yellow-50 tw-text-yellow-700',
  red: 'tw-bg-red-50 tw-text-red-600',
  blue: 'tw-bg-blue-50 tw-text-blue-600',
  gray: 'tw-bg-gray-100 tw-text-gray-600',
};

export function StatusPill({ tone = 'gray', children }) {
  return (
    <span
      className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-whitespace-nowrap ${
        PILL_TONES[tone] || PILL_TONES.gray
      }`}
    >
      {children}
    </span>
  );
}

// White card with an optional header row. noPadding lets a DataTable sit
// flush against the card edges.
export function SectionCard({
  title,
  description,
  actions,
  children,
  noPadding = false,
  className = '',
}) {
  return (
    <div
      className={`tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-sm tw-overflow-hidden ${className}`}
    >
      {title || actions ? (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3.5 tw-border-b tw-border-gray-100">
          <div className="tw-min-w-0">
            <h2 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-0">
              {title}
            </h2>
            {description ? (
              <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-mb-0">
                {description}
              </p>
            ) : null}
          </div>
          {actions ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      {noPadding ? children : <div className="tw-p-4">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="tw-px-6 tw-py-12 tw-text-center">
      {Icon ? (
        <Icon className="tw-w-12 tw-h-12 tw-text-gray-300 tw-mx-auto tw-mb-3" />
      ) : null}
      <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-1">
        {title}
      </p>
      {subtitle ? (
        <p className="tw-text-xs tw-text-gray-500 tw-mb-0">{subtitle}</p>
      ) : null}
      {action ? <div className="tw-mt-4">{action}</div> : null}
    </div>
  );
}
